import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormControlLabel, Tooltip, withStyles } from '@material-ui/core';
import c from 'classnames';
import { Helmet } from 'react-helmet';
import { cloneDeep } from 'lodash';
import AuthStore from '../../../common/AuthStore';
import FilterSvg from '../../Diagnose/assets/Filter.svg';
import ActiveFilter from '../../Diagnose/assets/ActiveFilters1.svg';
import { getStaticText } from '../../../utils/constants';
import './index.scss';
import Down from '../../Diagnose/assets/Down.svg';
import { getFilterName, getApiErrorMessage } from '../assets/functions';
import ErrorToast from '../../../components/InformationToast/ErrorToast';
import DisclaimerWarn from '../assets/DisclaimerWarning1.svg';
import MedianSplit from './MedianSplit';
import QuartileSplit from './QuartileSplit';
import WarningModal from '../Intro/WarningModal';
import { getFilterYear } from '../../../utils/functions';

function DifferentiatorBoard({
    toggleFilterView,
    defaultSettings,
    getDifferential,
    differentialDataFetched,
    differentialData,
    renderfilterApplied,
    filterParams,
    setDifferentialData,
    tab,
    setTab,
    differentialError,
    getPptData,
    filtersApplied,
    filters,
    defaultYear,
    getMedian,
    medianData,
    medianDataFetched,
    dataAtGlance,
    getDifferentialQuartile,
    showQuartileBtn,
    showQuatileTabBtn,
    showMedianTabBtn,
    showMedianBtn,
    getDifferentialMedian,
    setMedianData,
    medianError,
    warningModal,
    setWarningModal,
}) {
    const { lang } = defaultSettings;
    const staticText = getStaticText(lang);
    const { SITE_TEXT } = staticText;
    const {
        FILTERS,
        PRACTICE_RANK_PAGE_TEXT,
        NUMBER_CAP_SURVEY_TEXT,
        NUMBER_TEXT,
        DOWNLOAD,
        GSOD_SIG,
        SCORECARD_NOTE,
        DISCLAIMER,
        DIFFERENTIATOR_ANALYSIS_TABS,
        RESTRUCTION_TOOLTIP,
        MEDIAN_RESTRUCTION_TOOLTIP,
        QUARTILE_TAB,
        GSOD_PRAC_NOTE,
        GSOD_PRAC_ANALYSIS_MEDIAN,
        BMX_DIFFERENTIATOR_PPT_FOOTNOTE,
    } = SITE_TEXT || {};
    // const { n_survey = 0 } = rankingData || {};
    const [showNewData, setShowNewData] = useState(false);
    const [showThresholdBtn, setShowThresholdBtn] = useState(false);
    const { gsodRole = '' } = AuthStore;
    const gsodRoles = gsodRole === 'AccessLevels.asset_leader';
    const { total_survey = '', survey_completion: n_respondents = '' } = dataAtGlance;
    // const is24 = defaultYear === 2024;
    // const keys = filterParams.match(/\w+(?=\s*:)/g);
    // const keyCount = keys ? keys.length : 0;
    const filterYear = getFilterYear(filterParams) || defaultYear;
    const is24 = Number(filterYear || defaultYear) >= 2025;

    const getPpt = (slideArr = [], pptName = 'Differentiator') => {
        const { pptAccessToken = '' } = AuthStore;
        const output = {
            output_file_name: 'data.pptx',
            template_file_name: 'Template_OHIBMX_4_0.pptx',
            slides: slideArr,
        };
        const currentDate = new Date().toLocaleDateString();
        const currentTimestamp = new Date().toLocaleTimeString();
        const pptDifferentiatorName = `${pptName}_${currentDate}_${currentTimestamp}`;
        getPptData(output, pptAccessToken, pptDifferentiatorName);
    };

    const handleWarningModal = () => {
        setWarningModal(true);
    };

    // const [quartileValue, setQuartileValue] = useState(null);

    // useEffect(() => {
    //     if (total_survey) {
    //         const halfValue = total_survey / 2;
    //         const finalValue = halfValue / 2;
    //         setQuartileValue(finalValue);
    //     }
    //     // eslint-disable-next-line
    // }, []);

    const isThresholdSurveyError = message => {
        if (!message) {
            return false;
        }
        return (
            /minimum of \d+ surveys/i.test(message) ||
            /requires a minimum/i.test(message) ||
            /N size:\s*\d+/i.test(message) ||
            /selected only \d+ surveys/i.test(message) ||
            /Healthy organizations N size/i.test(message)
        );
    };

    const getThresholdTooltip = (errors, defaultTooltip) => {
        const message = getApiErrorMessage(errors);
        return message || defaultTooltip;
    };

    const getThresholdCheckboxDisabled = errors => {
        const message = getApiErrorMessage(errors);
        if (!message) {
            return false;
        }
        // Enable checkbox when API indicates minimum-survey / threshold restriction
        if (isThresholdSurveyError(message)) {
            return false;
        }
        return true;
    };

    const quartileThresholdTooltip = getThresholdTooltip(differentialError, RESTRUCTION_TOOLTIP);
    const medianThresholdTooltip = getThresholdTooltip(medianError, MEDIAN_RESTRUCTION_TOOLTIP);
    const isQuartileThresholdDisabled = getThresholdCheckboxDisabled(differentialError);
    const isMedianThresholdDisabled = getThresholdCheckboxDisabled(medianError);
    const activeApiErrorMessage = tab === 0 ? getApiErrorMessage(differentialError) : getApiErrorMessage(medianError);
    const hasQuartileApiError = !!getApiErrorMessage(differentialError);
    const hasMedianApiError = !!getApiErrorMessage(medianError);
    const hasActiveApiError = tab === 0 ? hasQuartileApiError : hasMedianApiError;

    const { records: quartileRecords = [] } = differentialData || {};
    const { records: medianRecords = [] } = medianData || {};
    const hasQuartileData = quartileRecords.length > 0;
    const hasMedianData = medianRecords.length > 0;
    const hasActiveData = tab === 0 ? hasQuartileData : hasMedianData;

    const isQuartileThresholdSelected = showThresholdBtn || showQuartileBtn;
    const isMedianThresholdSelected = showNewData || showMedianBtn;
    const isActiveThresholdSelected = tab === 0 ? isQuartileThresholdSelected : isMedianThresholdSelected;

    const shouldShowDownloadBtn = () => {
        if (!is24) return false;

        // Non-GSOD: hide on API error (scenario 1)
        if (!gsodRoles) return !hasActiveApiError;

        // GSOD: show when data is loaded
        if (hasActiveData) return true;

        // GSOD + API error + no data: only show after threshold checkbox is selected (scenario 2)
        if (hasActiveApiError) return isActiveThresholdSelected;

        return false;
    };
    const handleThreshold = ({ checked }) => {
        const checkedCondition = checked;
        if (!checkedCondition) {
            getMedian({ filters: filterParams, remove_threshold: showNewData });
            showMedianTabBtn(false);
        }
        setShowNewData(checked);
    };

    const handleThresholdBtn = ({ checked }) => {
        const checkedCondition = checked;
        if (!checkedCondition) {
            getDifferential({ filters: filterParams, remove_threshold: showThresholdBtn });
            showQuatileTabBtn(false);
        }
        setShowThresholdBtn(checked);
    };

    const LightTooltip = withStyles(theme => ({
        arrow: {
            color: theme.palette.common.black,
        },
        tooltip: {
            backgroundColor: '#333333',
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: 400,
        },
    }))(Tooltip);

    const dataObject = (row_index, value, style = 0, is_bold = 'false', alignment = 'center', rowspan) => {
        const obj = {
            row_index,
            column_index: 1,
            value,
            style,
            alignment,
            is_bold,
        };
        if (rowspan) {
            obj.rowspan = rowspan;
        }
        return obj;
    };

    const getDataForPpt = () => {
        const { records: mainData = [], n_size_tq = '', n_size_bq = '' } =
            tab === 0 ? differentialData : medianData || {};
        // Use default order (rank_tq asc) for PPT so UI sorting does not affect export
        const mainDataForPpt = cloneDeep(mainData).sort((a, b) => (a.rank_tq || 0) - (b.rank_tq || 0));
        const slidesArr = [];
        const selectedSample = getFilterName(filters);
        const sampleExclYear = getFilterName(
            filters.filter(({ filter_id: id = '' }) => !(id === 'year' || id === 'survey_year'))
        );
        const dataHealthy = [
            {
                name: tab === 0 ? 'median_text' : 'quartile_text',
                is_delete: true,
            },
            {
                name: 'n_size',
                value: `1. Healthy organizations reflect the top 25% of OHI scores in the selected sample (n=${n_size_tq})`,
            },
            {
                name: 'Checkme',
                is_delete: true,
            },
            {
                name: 'Source',
                value: `Source: Subset of organizations classified as healthiest organizations in selected sample. Selected sample=${selectedSample} (n=${n_respondents} no. of surveys=${total_survey})`,
            },
            {
                name: 'Source',
                value: `Source: Selected sample=${defaultYear},${sampleExclYear ||
                    'null'}, (n=${n_respondents} no. of surveys=${total_survey})`,
            },
        ];
        const dataUnhealthy = [
            {
                name: tab === 0 ? 'median_text' : 'quartile_text',
                is_delete: true,
            },
            {
                name: 'n_size',
                value: `1. Unhealthy organizations reflect the bottom 25% of OHI scores in the selected sample (n=${n_size_bq})`,
            },
            {
                name: 'Checkme',
                is_delete: true,
            },
            {
                name: 'Source',
                value: `Source: Selected sample=${defaultYear}, ${sampleExclYear ||
                    'null'}, (n=${n_respondents} no. of surveys=${total_survey})`,
            },
        ];
        const barchart = [
            {
                name: tab === 0 ? 'median_text' : 'quartile_text',
                is_delete: true,
            },
            {
                name: 'Checkme',
                is_delete: true,
            },
            {
                name: 'healthy_TextBox',
                value: `Healthy ${sampleExclYear} organizations`,
            },
            {
                name: 'unhealthy_TextBox',
                value: `Unhealthy ${sampleExclYear} organizations`,
            },
            {
                name: 'Footnote',
                value:
                    getFilterYear(filterParams) >= 2025
                        ? ` 1. ${BMX_DIFFERENTIATOR_PPT_FOOTNOTE[0]} \n 2. ${BMX_DIFFERENTIATOR_PPT_FOOTNOTE[1]} \n 3. ${BMX_DIFFERENTIATOR_PPT_FOOTNOTE[2]}`
                        : GSOD_PRAC_NOTE[1],
            },
            {
                name: 'Source',
                value: `Source: Selected sample=${defaultYear} ${sampleExclYear ||
                    'null'}, (n=${n_respondents} no. of surveys=${total_survey})`,
            },
        ];

        let rank_top = [];
        let rank_middle = [];
        let rank_bottom = [];
        let col = 0;

        mainDataForPpt.forEach(({ display_name }, i) => {
            if (i === 12 || i === 31) {
                col = 0;
            }
            if (i < 12) {
                rank_top.push(dataObject(col, display_name, 0, 'true'));
            } else if (i >= 12 && i < 31) {
                rank_middle.push(dataObject(col, display_name, 0));
            } else if (i >= 31) {
                rank_bottom.push(dataObject(col, display_name, 0));
            }
            col += 1;
        });
        dataHealthy.push(
            {
                name: 'rank_top',
                table_data: rank_top,
            },
            {
                name: 'rank_middle',
                table_data: rank_middle,
            },
            {
                name: 'rank_bottom',
                table_data: rank_bottom,
            }
        );
        const dataCopy = cloneDeep(mainDataForPpt);
        const newItem = dataCopy.sort((a, b) => a.rank_bq - b.rank_bq);
        col = 0;
        rank_top = [];
        rank_middle = [];
        rank_bottom = [];
        newItem.forEach(({ display_name }, i) => {
            if (i === 12 || i === 31) {
                col = 0;
            }
            if (i < 12) {
                rank_top.push(dataObject(col, display_name, 0, 'true'));
            } else if (i >= 12 && i < 31) {
                rank_middle.push(dataObject(col, display_name, 0));
            } else if (i >= 31) {
                rank_bottom.push(dataObject(col, display_name, 0));
            }
            col += 1;
        });
        dataUnhealthy.push(
            {
                name: 'rank_top',
                table_data: rank_top,
            },
            {
                name: 'rank_middle',
                table_data: rank_middle,
            },
            {
                name: 'rank_bottom',
                table_data: rank_bottom,
            }
        );

        col = 0;
        mainDataForPpt.forEach(({ display_name, rank_bq, rank_tq, sig_diff_tq_bq }) => {
            const { text = '' } = GSOD_SIG[sig_diff_tq_bq];
            if (text === 'stronger') {
                col += 1;
                barchart.push(
                    {
                        name: `healthy_${col}`,
                        value: display_name,
                    },
                    {
                        name: `healthy_${col}_tq`,
                        value: rank_tq,
                    },
                    {
                        name: `healthy_${col}_bq`,
                        value: rank_bq,
                    }
                );
            }
        });

        col += 1;
        while (col <= 12) {
            barchart.push(
                {
                    name: `healthy_${col}`,
                    is_delete: true,
                },
                {
                    name: `healthy_${col}_tq`,
                    is_delete: true,
                },
                {
                    name: `healthy_${col}_bq`,
                    is_delete: true,
                }
            );
            col += 1;
        }

        col = 0;
        mainDataForPpt.forEach(({ display_name, rank_tq, rank_bq, sig_diff_tq_bq }) => {
            const { text = '' } = GSOD_SIG[sig_diff_tq_bq];
            if (text === 'weaker') {
                col += 1;
                barchart.push(
                    {
                        name: `unhealthy_${col}`,
                        value: display_name,
                    },
                    {
                        name: `unhealthy_${col}_tq`,
                        value: rank_tq,
                    },
                    {
                        name: `unhealthy_${col}_bq`,
                        value: rank_bq,
                    }
                );
            }
        });

        col += 1;
        while (col <= 12) {
            barchart.push(
                {
                    name: `unhealthy_${col}`,
                    is_delete: true,
                },
                {
                    name: `unhealthy_${col}_tq`,
                    is_delete: true,
                },
                {
                    name: `unhealthy_${col}_bq`,
                    is_delete: true,
                }
            );
            col += 1;
        }

        slidesArr.push(
            {
                identify_shape_name: 'Checkme',
                identify_shape_value: 'Differentiator_healthy',
                data: dataHealthy,
                style: [
                    {
                        bgcolor: '#ffffff',
                        text_color: '#000000',
                    },
                ],
            },
            {
                identify_shape_name: 'Checkme',
                identify_shape_value: 'Differentiator_unhealthy',
                data: dataUnhealthy,
                style: [
                    {
                        bgcolor: '#ffffff',
                        text_color: '#16537e',
                    },
                ],
            },
            {
                identify_shape_name: 'Checkme',
                identify_shape_value: 'Differentiator_barchart',
                data: barchart,
                style: [
                    {
                        bgcolor: '#ffffff',
                        text_color: '#16537e',
                    },
                ],
            }
        );
        getPpt([...slidesArr], `differentiator_analysis`);
    };

    const getDataForPptMedian = () => {
        const { records: mainData = [], n_size_tq = '', n_size_bq = '' } = medianData || {};
        // Use default order (rank_tq asc) for PPT so UI sorting does not affect export
        const mainDataForPpt = cloneDeep(mainData).sort((a, b) => (a.rank_tq || 0) - (b.rank_tq || 0));
        const slidesArr = [];
        const selectedSample = getFilterName(filters);
        const sampleExclYear = getFilterName(
            filters.filter(({ filter_id: id = '' }) => !(id === 'year' || id === 'survey_year'))
        );
        const dataHealthy = [
            {
                name: 'n_size',
                value: `1. Healthy organizations reflect the top 50% of OHI scores in the selected sample (n=${n_size_tq})`,
            },
            {
                name: 'Checkme',
                is_delete: true,
            },
            {
                name: 'Source',
                value: `Source: Subset of organizations classified as healthiest organizations in selected sample. Selected sample=${selectedSample} (n=${n_respondents} no. of surveys=${total_survey})`,
            },
        ];
        const dataUnhealthy = [
            {
                name: 'n_size',
                value: `1. Unhealthy organizations reflect the bottom 50% of OHI scores in the selected sample (n=${n_size_bq})`,
            },
            {
                name: 'Checkme',
                is_delete: true,
            },
            {
                name: 'Source',
                value: `Source: Subset of organizations classified as unhealthiest organizations in selected sample. Selected sample=${selectedSample} (n=${n_respondents} no. of surveys=${total_survey})`,
            },
        ];
        const barchart = [
            {
                name: 'Checkme',
                is_delete: true,
            },
            {
                name: 'healthy_TextBox',
                value: `Healthy ${sampleExclYear} organizations`,
            },
            {
                name: 'unhealthy_TextBox',
                value: `Unhealthy ${sampleExclYear} organizations`,
            },
            {
                name: 'Footnote',
                value:
                    getFilterYear(filterParams) >= 2025
                        ? ` 1. ${BMX_DIFFERENTIATOR_PPT_FOOTNOTE[0]} \n 2. ${BMX_DIFFERENTIATOR_PPT_FOOTNOTE[1]}\n 3. ${BMX_DIFFERENTIATOR_PPT_FOOTNOTE[2]}`
                        : GSOD_PRAC_NOTE[1],
            },
            {
                name: 'Source',
                value: `Source: McKinsey & Company | People and Organizational Performance Practice | OHI database ${defaultYear}, # respondents=${n_respondents} ${selectedSample} n=${total_survey}`,
            },
        ];

        let rank_top = [];
        let rank_middle = [];
        let rank_bottom = [];
        let col = 0;

        mainDataForPpt.forEach(({ display_name }, i) => {
            if (i === 12 || i === 31) {
                col = 0;
            }
            if (i < 12) {
                rank_top.push(dataObject(col, display_name, 0, 'true'));
            } else if (i >= 12 && i < 31) {
                rank_middle.push(dataObject(col, display_name, 0));
            } else if (i >= 31) {
                rank_bottom.push(dataObject(col, display_name, 0));
            }
            col += 1;
        });
        dataHealthy.push(
            {
                name: 'rank_top',
                table_data: rank_top,
            },
            {
                name: 'rank_middle',
                table_data: rank_middle,
            },
            {
                name: 'rank_bottom',
                table_data: rank_bottom,
            }
        );
        const dataCopy = cloneDeep(mainDataForPpt);
        const newItem = dataCopy.sort((a, b) => a.rank_bq - b.rank_bq);
        col = 0;
        rank_top = [];
        rank_middle = [];
        rank_bottom = [];
        newItem.forEach(({ display_name }, i) => {
            if (i === 12 || i === 31) {
                col = 0;
            }
            if (i < 12) {
                rank_top.push(dataObject(col, display_name, 0, 'true'));
            } else if (i >= 12 && i < 31) {
                rank_middle.push(dataObject(col, display_name, 0));
            } else if (i >= 31) {
                rank_bottom.push(dataObject(col, display_name, 0));
            }
            col += 1;
        });
        dataUnhealthy.push(
            {
                name: 'rank_top',
                table_data: rank_top,
            },
            {
                name: 'rank_middle',
                table_data: rank_middle,
            },
            {
                name: 'rank_bottom',
                table_data: rank_bottom,
            }
        );

        col = 0;
        mainDataForPpt.forEach(({ display_name, rank_bq, rank_tq, sig_diff_tq_bq }) => {
            const { text = '' } = GSOD_SIG[sig_diff_tq_bq];
            if (text === 'stronger') {
                col += 1;
                barchart.push(
                    {
                        name: `healthy_${col}`,
                        value: display_name,
                    },
                    {
                        name: `healthy_${col}_tq`,
                        value: rank_tq,
                    },
                    {
                        name: `healthy_${col}_bq`,
                        value: rank_bq,
                    }
                );
            }
        });

        col += 1;
        while (col <= 12) {
            barchart.push(
                {
                    name: `healthy_${col}`,
                    is_delete: true,
                },
                {
                    name: `healthy_${col}_tq`,
                    is_delete: true,
                },
                {
                    name: `healthy_${col}_bq`,
                    is_delete: true,
                }
            );
            col += 1;
        }

        col = 0;
        mainDataForPpt.forEach(({ display_name, rank_tq, rank_bq, sig_diff_tq_bq }) => {
            const { text = '' } = GSOD_SIG[sig_diff_tq_bq];
            if (text === 'weaker') {
                col += 1;
                barchart.push(
                    {
                        name: `unhealthy_${col}`,
                        value: display_name,
                    },
                    {
                        name: `unhealthy_${col}_tq`,
                        value: rank_tq,
                    },
                    {
                        name: `unhealthy_${col}_bq`,
                        value: rank_bq,
                    }
                );
            }
        });

        col += 1;
        while (col <= 12) {
            barchart.push(
                {
                    name: `unhealthy_${col}`,
                    is_delete: true,
                },
                {
                    name: `unhealthy_${col}_tq`,
                    is_delete: true,
                },
                {
                    name: `unhealthy_${col}_bq`,
                    is_delete: true,
                }
            );
            col += 1;
        }

        slidesArr.push(
            {
                identify_shape_name: 'Checkme',
                identify_shape_value: 'Differentiator_healthy',
                data: dataHealthy,
                style: [
                    {
                        bgcolor: '#ffffff',
                        text_color: '#000000',
                    },
                ],
            },
            {
                identify_shape_name: 'Checkme',
                identify_shape_value: 'Differentiator_unhealthy',
                data: dataUnhealthy,
                style: [
                    {
                        bgcolor: '#ffffff',
                        text_color: '#16537e',
                    },
                ],
            },
            {
                identify_shape_name: 'Checkme',
                identify_shape_value: 'Differentiator_barchart',
                data: barchart,
                style: [
                    {
                        bgcolor: '#ffffff',
                        text_color: '#16537e',
                    },
                ],
            }
        );
        getPpt([...slidesArr], `differentiator_analysis`);
    };

    const getPage = () => {
        switch (tab) {
            case 0:
                return (
                    <QuartileSplit
                        getDifferential={getDifferential}
                        differentialData={differentialData}
                        differentialDataFetched={differentialDataFetched}
                        filterParams={filterParams}
                        setDifferentialData={setDifferentialData}
                        defaultSettings={defaultSettings}
                        error={differentialError}
                        pracRankingTab={tab}
                        showThresholdBtn={showThresholdBtn}
                        dataAtGlance={dataAtGlance}
                        getDifferentialQuartile={getDifferentialQuartile}
                        showQuartileBtn={showQuartileBtn}
                        warningModal={warningModal}
                        setWarningModal={setWarningModal}
                    />
                );
            case 1:
                return (
                    <MedianSplit
                        getMedian={getMedian}
                        medianData={medianData}
                        medianDataFetched={medianDataFetched}
                        filterParams={filterParams}
                        setMedianData={setMedianData}
                        defaultSettings={defaultSettings}
                        error={medianError}
                        pracRankingTab={tab}
                        showNewData={showNewData}
                        showMedianBtn={showMedianBtn}
                        showMedianTabBtn={showMedianTabBtn}
                        getDifferentialMedian={getDifferentialMedian}
                        warningModal={warningModal}
                        setWarningModal={setWarningModal}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="practiceBoards clearfix">
                <div className="practiceContentFix">
                    <Helmet>
                        <title>{PRACTICE_RANK_PAGE_TEXT['5']}</title>
                    </Helmet>
                    <div className="rightScorecardHeader">
                        <div className="scorecardHeading">
                            <div className="mainHeading">Differentiator Analysis</div>
                            <div className="nRes">{`${NUMBER_CAP_SURVEY_TEXT} (${NUMBER_TEXT}) : ${
                                // tab === 0 ? n_survey : differentialDataSize
                                total_survey
                            }`}</div>
                        </div>
                        <ul className="navScoreList">
                            {tab === 0 && shouldShowDownloadBtn() ? (
                                <li data-testid="getPptBtn" onClick={getDataForPpt}>
                                    <img src={Down} alt="" />
                                    <span className="lineHead upperHead">{DOWNLOAD}</span>
                                </li>
                            ) : null}

                            {tab === 1 && shouldShowDownloadBtn() ? (
                                <li data-testid="getPptBtn" onClick={getDataForPptMedian}>
                                    <img src={Down} alt="" />
                                    <span className="lineHead upperHead">{DOWNLOAD}</span>
                                </li>
                            ) : null}
                            <li onClick={() => toggleFilterView(true)} data-testid="filterButton">
                                <img src={!filtersApplied ? FilterSvg : ActiveFilter} alt="" />{' '}
                                <span className="filter">{FILTERS}</span>
                            </li>
                        </ul>
                    </div>
                    <div className="disclaimerContent">
                        <img src={DisclaimerWarn} alt="" />
                        <div className="note scoreCardNote">
                            <span>{DISCLAIMER} : </span>
                            <div>{SCORECARD_NOTE}</div>
                            <div className="warningContent" onClick={handleWarningModal}>
                                Know more
                            </div>
                        </div>
                    </div>
                    {renderfilterApplied}
                    <div className="optionWrapper">
                        <div className="differentiatorToggle">
                            {gsodRoles ? (
                                <ul className="leftNav">
                                    {DIFFERENTIATOR_ANALYSIS_TABS.map((tabName, index) => (
                                        <li className={c({ activeLink: tab === index })} onClick={() => setTab(index)}>
                                            {tabName}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <ul className="leftNav">
                                    {QUARTILE_TAB.map((tabName, index) => (
                                        <li className={c({ activeLink: tab === index })} onClick={() => setTab(index)}>
                                            {tabName}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {tab === 1 && gsodRoles ? (
                                <div className="showPracticeToradoChart checkboxTip">
                                    <LightTooltip title={medianThresholdTooltip} arrow placement="top">
                                        <span className="threshold-tooltip-wrap">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={showNewData || showMedianBtn}
                                                        onChange={({ target }) => handleThreshold(target)}
                                                        name="tresholdData"
                                                        type="checkbox"
                                                        disabled={isMedianThresholdDisabled}
                                                    />
                                                }
                                                label="Remove threshold restriction"
                                            />
                                        </span>
                                    </LightTooltip>
                                </div>
                            ) : null}

                            {tab === 0 && gsodRoles ? (
                                <div className="showPracticeToradoChart checkboxTip">
                                    <LightTooltip title={quartileThresholdTooltip} arrow placement="top">
                                        <span className="threshold-tooltip-wrap">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={showThresholdBtn || showQuartileBtn}
                                                        onChange={({ target }) => handleThresholdBtn(target)}
                                                        name="tresholdData"
                                                        type="checkbox"
                                                        disabled={isQuartileThresholdDisabled}
                                                    />
                                                }
                                                label="Remove threshold restriction"
                                            />
                                        </span>
                                    </LightTooltip>
                                </div>
                            ) : null}
                        </div>
                        <div className="rightOptions">
                            {/* <div>
                        <ul className="rightSideNav">
                            <li className="strong">{`Number of Surveys (n) = ${n_survey}`}</li>
                            <li
                                onClick={() => toggleFilterView(true)}
                                data-testid="filterButton"
                                className="filterButton"
                            >
                                <img src={filtersApplied ? ActiveFilter : FilterSvg} alt="" />
                                {FILTERS}
                            </li>
                        </ul>
                    </div> */}
                        </div>
                    </div>
                </div>
                {getPage()}
            </div>

            {warningModal ? <WarningModal setWarningModal={setWarningModal} /> : null}
            {activeApiErrorMessage ? (
                <ErrorToast
                    key={`${tab}-${activeApiErrorMessage}`}
                    message={activeApiErrorMessage}
                    showHeaderMessage={false}
                />
            ) : null}
        </>
    );
}

DifferentiatorBoard.propTypes = {
    defaultSettings: PropTypes.object.isRequired,
    toggleFilterView: PropTypes.func.isRequired,
    differentialData: PropTypes.object.isRequired,
    differentialDataFetched: PropTypes.bool.isRequired,
    getDifferential: PropTypes.func.isRequired,
    renderfilterApplied: PropTypes.string.isRequired,
    filterParams: PropTypes.string.isRequired,
    setDifferentialData: PropTypes.func.isRequired,
    tab: PropTypes.number.isRequired,
    setTab: PropTypes.func.isRequired,
    differentialError: PropTypes.array.isRequired,
    dataAtGlance: PropTypes.object.isRequired,
    filtersApplied: PropTypes.bool.isRequired,
    defaultYear: PropTypes.number.isRequired,
    showQuartileBtn: PropTypes.bool.isRequired,
    setMedianData: PropTypes.func.isRequired,
    showQuatileTabBtn: PropTypes.func.isRequired,
    showMedianBtn: PropTypes.bool.isRequired,
    showMedianTabBtn: PropTypes.func.isRequired,
    getDifferentialMedian: PropTypes.func.isRequired,
    getMedian: PropTypes.func.isRequired,
    medianData: PropTypes.object.isRequired,
    medianDataFetched: PropTypes.bool.isRequired,
    getDifferentialQuartile: PropTypes.func.isRequired,
    filters: PropTypes.array.isRequired,
    getPptData: PropTypes.func.isRequired,
    medianError: PropTypes.array.isRequired,
    warningModal: PropTypes.bool.isRequired,
    setWarningModal: PropTypes.func.isRequired,
};

export default DifferentiatorBoard;
